import React, { memo, useMemo, lazy, Suspense, useContext, useEffect } from 'react'
import { Loading } from 'components/shared'
import { useSelector } from 'react-redux'
import {
    LAYOUT_TYPE_CLASSIC,
    LAYOUT_TYPE_MODERN,
    LAYOUT_TYPE_SIMPLE,
    LAYOUT_TYPE_STACKED_SIDE,
    LAYOUT_TYPE_DECKED,
    LAYOUT_TYPE_BLANK,
} from 'constants/theme.constant'
import useDirection from 'utils/hooks/useDirection'
import useLocale from 'utils/hooks/useLocale'
import { ModalContext } from 'contexts/ModalContext'
import { Dialog, Notification, toast } from 'components/ui'
import { SessionContext } from 'contexts/SessionContext'
import { UploadContext } from 'contexts/UploadContext'
import { BulkProgressContext } from 'contexts/BulkProgressContext'
import UploadModal from './UploadModal'
import BulkProgressModal from './BulkProgressModal'
import MediaUpload from 'views/calendar/MediaUpload'
import { PriorityModalContext } from 'contexts/PriorityModalContext'
import useBus from 'use-bus'
import OutOfUsersModal from './OutOfUsersModal'
import OutOfCompaniesModal from './OutOfCompaniesModal'
import OutOfWordsModal from './OutOfWordsModal'
import { CompanyContext } from 'contexts/CompanyContext'
import Subscription from 'views/subscription'
import capitalize from 'components/ui/utils/capitalize'
import OutOfImagesModal from './OutOfImagesModal'
import AppSumoAddonModal from './AppSumoAddonModal'
import PlusModal from 'components/shared/PlusModal'
import EndTrialModal from './EndTrialModal'
import { AppSumoContext } from 'contexts/AppSumoContext'
import CompanyOnboardingModal from 'components/company_onboarding/CompanyOnboardingModal'
import { COMPANY_ONBOARDING_REQUIRED, REQUIRE_PAYMENT, APPSUMO_ADDON_ENABLED } from 'components/ui/utils/constant'
import { CompanyOnboardingContext } from 'contexts/CompanyOnboardingContext'
import { useWhitelabel } from 'contexts/WhitelabelThemeContext'
import GenerationProgressIndicator from 'components/shared/GenerationProgressIndicator'
import UniversalCreateModal from 'components/shared/UniversalCreateModal'
import { useNavigate } from 'react-router-dom'

const layouts = {
    [LAYOUT_TYPE_CLASSIC]: lazy(() => import('./ClassicLayout')),
    [LAYOUT_TYPE_MODERN]: lazy(() => import('./ModernLayout')),
    [LAYOUT_TYPE_STACKED_SIDE]: lazy(() => import('./StackedSideLayout')),
    [LAYOUT_TYPE_SIMPLE]: lazy(() => import('./SimpleLayout')),
    [LAYOUT_TYPE_DECKED]: lazy(() => import('./DeckedLayout')),
    [LAYOUT_TYPE_BLANK]: lazy(() => import('./BlankLayout')),
}

const Layout = () => {
    const layoutType = useSelector((state) => state.theme.layout.type)

    const { isCompleted: isOnboardingCompleted, isOpened: isOnboardingModalOpened } = useContext(CompanyOnboardingContext);
    const { isOpened, closeModal, component, dialogWidth, hasCloseButton, isClosable, contentClassName } = useContext(ModalContext);
    const { isOpenedPriority, openModalPriority, closeModalPriority, componentPriority, dialogWidthPriority, hasCloseButtonPriority, isClosablePriority, contentClassNamePriority, priorityModalRef } = useContext(PriorityModalContext);
    const { isOpenedUploadModal } = useContext(UploadContext);
    const { isOpened: isOpenedBulkProgress } = useContext(BulkProgressContext);
    const { user, session } = useContext(SessionContext);
    const { company, mainUser } = useContext(CompanyContext);
    const { isLicensee } = useContext(AppSumoContext);
    const { isWhitelabel } = useWhitelabel();
    const navigate = useNavigate();

    useDirection()
    useLocale()

    // Standalone full-screen pages — bypass all layout chrome
    const isStandalonePage = ['/appsumo', '/appsumo-license'].includes(window.location.pathname);

    const AppLayout = useMemo(() => {
        if (isStandalonePage) {
            return lazy(() => import('./BlankLayout'))
        }
        if (session?.isLoggedIn) {
            return layouts[layoutType]
        }
        return lazy(() => import('./AuthLayout'))
    }, [layoutType, session, isStandalonePage]);

    useBus('subscriptionAddOnModal', (action) => {
        const entity = action.payload.entity;
        const formattedEntity = capitalize(entity == "bytes" ? "storage" : entity);

        // Check if user is on free trial
        const freeTrialDate = user?.subscription?.trial_ends_at;
        const isFreeTrial = freeTrialDate ? new Date(freeTrialDate).getTime() > new Date().getTime() : false;

        toast.push(<Notification type="warning" title={`You need to upgrade your plan in order to use more ${formattedEntity}.`} />, { placement: "top-center" });
        
        if (mainUser?.id == user?.id || entity == "companies") {
            // If user is on free trial and has a subscription, show EndTrialModal
            if (isFreeTrial && mainUser?.subscription) {
                openModalPriority(<EndTrialModal />, 650, true, true, "relative !overflow-visible !overflow-y-visible !p-0 !bg-transparent");
            } else if (
                !mainUser?.subscription || 
                entity == "posts" || 
                entity == "designs" || 
                entity == "bytes"
            ) {
                openModalPriority(<Subscription isModal={true} />, 1200, true, true, `!p-0 !bg-tertiary-100 dark:!bg-gray-900 zoom-95`);
            } else if (entity == "users") {
                openModalPriority(<OutOfUsersModal />);
            } else if (entity == "companies") {
                openModalPriority(<OutOfCompaniesModal />);
            } else if (entity == "words") {
                openModalPriority(<OutOfWordsModal />);
            } else if (entity == "images") {
                if (APPSUMO_ADDON_ENABLED && isLicensee()) {
                    openModalPriority(<AppSumoAddonModal />);
                } else {
                    openModalPriority(<OutOfImagesModal />);
                }
            } else if (entity == "premium_images" && !isWhitelabel) {
                openModalPriority(<PlusModal />, 650, true, true, "relative !overflow-visible !overflow-y-visible !p-0 !bg-transparent");
            }
        }
    }, [mainUser?.id, user?.pm_last_four, mainUser?.subscription?.stripe_status, user?.subscription?.trial_ends_at]);

    function appHeight() {
        const doc = document.documentElement;
        doc.style.setProperty('--safe-area-px', `0px`);
    }

    // Set app height
    useEffect(() => {
        window.addEventListener('resize', appHeight);
        appHeight();
    }, [document?.getElementsByTagName("main")?.[0]?.offsetHeight])

    // If not subscribed and require payment => open subscription modal
    useEffect(() => {
        if (
            !window.location.pathname.includes("onboarding") && 
            !window.location.pathname.includes("appsumo") && 
            !window.location.pathname.includes("subscription") &&  
            !window.location.pathname.includes("settings") &&
            mainUser?.id == user?.id && 
            !!company?.id && 
            !mainUser?.subscription &&
            !isLicensee() &&
            (!!REQUIRE_PAYMENT && !user?.companies?.filter(item => item.role.name != 'admin' && item.role.name != 'owner')?.length) &&
            (
                (
                    COMPANY_ONBOARDING_REQUIRED &&
                    isOnboardingCompleted &&
                    !isOnboardingModalOpened
                ) || (
                    !COMPANY_ONBOARDING_REQUIRED
                )
            )
        ) {
            if (user?.companies?.some(company => (company?.user?.id || company?.user_id) != user?.id)) {
                if (!window.location.pathname.includes("subscription")) {
                    navigate("/subscription");
                }
            } else {
                openModalPriority(
                    <Subscription isModal={true} />, 
                    1200, 
                    false, 
                    false, 
                    `!p-0 !bg-tertiary-100 dark:!bg-gray-900 zoom-95`
                );
            }
        }
    }, [mainUser?.subscription, mainUser?.id, user?.id, company?.id, isOnboardingCompleted, isOnboardingModalOpened, window.location.pathname]);

    return (
        <Suspense
            fallback={
                <div className="flex flex-auto flex-col h-[100vh]">
                    <Loading loading={true} />
                </div>
            }
        >
            <AppLayout />

            <Dialog 
                isOpen={isOpened} 
                onClose={closeModal} 
                onRequestClose={closeModal} 
                closeButtonClassName={`${!hasCloseButton ? `hidden` : ``}`} 
                overlayClassName="flex items-center justify-center" 
                contentClassName={contentClassName}
                width={dialogWidth ?? undefined} 
                closable={isClosable}
            >
                {component}
            </Dialog>

            <Dialog ref={priorityModalRef} isOpen={isOpenedPriority} onClose={closeModalPriority} onRequestClose={closeModalPriority} closeButtonClassName={`${!hasCloseButtonPriority ? `hidden` : ``}`} overlayClassName="flex items-center justify-center z-[10000000]" width={dialogWidthPriority ?? undefined} closable={isClosablePriority} contentClassName={contentClassNamePriority}>
                {componentPriority}
            </Dialog>

            <MediaUpload invisible={true} />

            {isOpenedUploadModal &&
                <UploadModal />
            }

            {isOpenedBulkProgress &&
                <BulkProgressModal />
            }

            <CompanyOnboardingModal />
            
            {/* Global Generation Progress Indicator */}
            <GenerationProgressIndicator />

            {/* Universal Create Modal */}
            <UniversalCreateModal />
        </Suspense>
    )
}

export default memo(Layout)
